/**
 * Omfattende spørsmålsbank for Workplace Assessment Engine.
 * Strukturert i 12 dimensjoner ihht. organisasjonspsykologiske modeller.
 */
export const assessmentDimensions = [
  {
    id: 'workload',
    name: 'Workload & Demands',
    description: 'Vurdering av kognitiv og operativ belastning.',
    questions: [
      { id: 'w1', text: 'Arbeidsmengden min er håndterbar innenfor normal arbeidstid.', reversed: false },
      { id: 'w2', text: 'Jeg opplever ofte at kravene til meg er motstridende.', reversed: true },
      { id: 'w3', text: 'Jeg har nok tid til å utføre oppgavene mine med god kvalitet.', reversed: false },
      { id: 'w4', text: 'Arbeidstempoet er så høyt at det går ut over helsen min.', reversed: true },
      { id: 'w5', text: 'Jeg føler meg ofte emosjonelt utmattet etter en arbeidsdag.', reversed: true }
    ]
  },
  {
    id: 'role_clarity',
    name: 'Role Clarity',
    description: 'Klarhet i ansvar, mål og forventninger.',
    questions: [
      { id: 'rc1', text: 'Jeg har klare mål for arbeidet mitt.', reversed: false },
      { id: 'rc2', text: 'Det er tydelig hvem som har beslutningsmyndighet i min avdeling.', reversed: false },
      { id: 'rc3', text: 'Jeg vet nøyaktig hva som forventes av meg i rollen min.', reversed: false },
      { id: 'rc4', text: 'Jeg opplever ofte usikkerhet om mine ansvarsområder.', reversed: true }
    ]
  },
  {
    id: 'leadership',
    name: 'Leadership Support',
    description: 'Nærmeste leders evne til støtte og veiledning.',
    questions: [
      { id: 'ls1', text: 'Min nærmeste leder er tilgjengelig når jeg trenger støtte.', reversed: false },
      { id: 'ls2', text: 'Lederen min gir meg konstruktiv og lærerik feedback.', reversed: false },
      { id: 'ls3', text: 'Jeg føler at lederen min har tillit til mine vurderinger.', reversed: false },
      { id: 'ls4', text: 'Lederen min tar tak i arbeidsmiljøutfordringer på en god måte.', reversed: false }
    ]
  },
  {
    id: 'collaboration',
    name: 'Team Collaboration',
    description: 'Samarbeidsklima og gjensidig støtte.',
    questions: [
      { id: 'tc1', text: 'I mitt team hjelper vi hverandre når det er mye å gjøre.', reversed: false },
      { id: 'tc2', text: 'Det er god informasjonsflyt mellom kollegene mine.', reversed: false },
      { id: 'tc3', text: 'Vi drar nytte av hverandres styrker og kompetanse.', reversed: false },
      { id: 'tc4', text: 'Det er lav terskel for å be om hjelp i teamet.', reversed: false }
    ]
  },
  {
    id: 'psych_safety',
    name: 'Psychological Safety',
    description: 'Trygghet for å ytre seg og lære av feil.',
    questions: [
      { id: 'ps1', text: 'Det er trygt å ta en risiko i dette teamet.', reversed: false },
      { id: 'ps2', text: 'Hvis jeg gjør en feil, blir det ofte brukt mot meg.', reversed: true },
      { id: 'ps3', text: 'Jeg kan ta opp vanskelige temaer og utfordringer åpent.', reversed: false },
      { id: 'ps4', text: 'Ingen i teamet ville bevisst undergravd mine ideer.', reversed: false }
    ]
  },
  {
    id: 'culture',
    name: 'Workplace Culture',
    description: 'Verdier, respekt og inkludering.',
    questions: [
      { id: 'wc1', text: 'Arbeidsplassen preges av gjensidig respekt.', reversed: false },
      { id: 'wc2', text: 'Vi feirer suksesser og gode prestasjoner sammen.', reversed: false },
      { id: 'wc3', text: 'Alle ansatte behandles likt uavhengig av bakgrunn.', reversed: false },
      { id: 'wc4', text: 'Jeg føler stolthet over å jobbe i denne virksomheten.', reversed: false }
    ]
  },
  {
    id: 'conflict',
    name: 'Conflict & Tension',
    description: 'Forekomst og håndtering av gnisninger.',
    questions: [
      { id: 'ct1', text: 'Det er mye negative spenninger mellom folk her.', reversed: true },
      { id: 'ct2', text: 'Uenigheter håndteres på en profesjonell og saklig måte.', reversed: false },
      { id: 'ct3', text: 'Jeg opplever at det er mye "baksnakking" i gangene.', reversed: true },
      { id: 'ct4', text: 'Konflikter får lov til å ligge for lenge før de løses.', reversed: true }
    ]
  },
  {
    id: 'autonomy',
    name: 'Autonomy & Control',
    description: 'Innflytelse over egen arbeidshverdag.',
    questions: [
      { id: 'ac1', text: 'Jeg har stor innflytelse på hvordan jeg løser oppgavene mine.', reversed: false },
      { id: 'ac2', text: 'Jeg kan selv planlegge min egen arbeidshverdag.', reversed: false },
      { id: 'ac3', text: 'Beslutninger som påvirker mitt arbeid tas over hodet på meg.', reversed: true }
    ]
  },
  {
    id: 'communication',
    name: 'Communication',
    description: 'Gjennomsiktighet og informasjonsdeling.',
    questions: [
      { id: 'cm1', text: 'Ledelsen er åpen om viktige beslutninger i selskapet.', reversed: false },
      { id: 'cm2', text: 'Jeg får den informasjonen jeg trenger for å gjøre en god jobb.', reversed: false },
      { id: 'cm3', text: 'Det er lett å få kontakt med folk i andre avdelinger.', reversed: false }
    ]
  },
  {
    id: 'change',
    name: 'Organizational Change',
    description: 'Håndtering av omstilling og utvikling.',
    questions: [
      { id: 'oc1', text: 'Endringer kommuniseres i god tid før de skjer.', reversed: false },
      { id: 'oc2', text: 'Jeg føler meg involvert i endringsprosesser som angår meg.', reversed: false },
      { id: 'oc3', text: 'Organisasjonen er god til å lære av tidligere endringer.', reversed: false }
    ]
  },
  {
    id: 'recognition',
    name: 'Recognition & Feedback',
    description: 'Verdsettelse og tilbakemeldingskultur.',
    questions: [
      { id: 'rf1', text: 'Jeg får anerkjennelse når jeg har gjort en god jobb.', reversed: false },
      { id: 'rf2', text: 'Det er kultur for å gi hverandre positive tilbakemeldinger.', reversed: false },
      { id: 'rf3', text: 'Min innsats blir lagt merke til av ledelsen.', reversed: false }
    ]
  },
  {
    id: 'stress_recovery',
    name: 'Stress & Recovery',
    description: 'Balanse mellom jobb, fritid og hvile.',
    questions: [
      { id: 'sr1', text: 'Jeg klarer å koble helt av fra jobb når jeg har fri.', reversed: false },
      { id: 'sr2', text: 'Jeg føler meg ofte uthvilt og klar for en ny arbeidsdag.', reversed: false },
      { id: 'sr3', text: 'Arbeidet mitt går ofte ut over privatlivet mitt.', reversed: true },
      { id: 'sr4', text: 'Jeg har mulighet til å ta nødvendige pauser i løpet av dagen.', reversed: false }
    ]
  }
];